import React, { useState } from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';
import pluralize from 'pluralize';

import {
  LISTING_OCCUPANT_TYPE_VACANT,
  LISTING_PROPERTY_OWNER_INSTITUTION,
  LISTING_STATUS_ACTIVE,
} from 'common/listingConstants';
import { hasRoleAdmin } from 'common/userRoles';

import useBranch from 'client/hooks/useBranch';
import useDispatch from 'client/hooks/useDispatch';

import SrFlex from 'client/components/SrFlex';
import SrFlexRow from 'client/components/SrFlexRow/SrFlexRow';
import SrLink from 'client/components/SrLink/SrLink';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrIcon from 'client/components/SrIcon';
import SrTag, {
  SR_TAG_THEME_LIGHTBLUE_BLACK,
  SR_TAG_THEME_LIGHTBLUE_BLACK_HOVER,
  SR_TAG_THEME_LIGHTGREEN_GREEN,
  SR_TAG_THEME_ORANGE_WHITE,
  SR_TAG_THEME_RED_WHITE,
  SR_TAG_THEME_WHITE_BLACK,
} from 'client/components/SrTag/SrTag';
import SrAdminListingNotes from 'client/components/SrAdminListingNotes';
import SrCard, { SR_CARD_BULLET_COLOR_BLUE } from 'client/components/SrCard/SrCard';
import SrModalV2, { MODAL_THEME_STACK } from 'client/components/SrModalV2/SrModalV2';

import './SrCompletedSetupsTable.less';

const SrCompletedSetupsTable = ({
  completedSetups,
  handleIsTourableUpdate,
  handleIsLiveUpdate,
  partnerSlug,
  reloadItems,
}) => {
  const dispatch = useDispatch();
  const { currentUser } = useBranch({
    currentUser: ['currentUser'],
  });
  const [listingNotesModal, setListingNotesModal] = useState(null);
  const isAdmin = hasRoleAdmin(currentUser.roles);

  const _getSetupAddressComponent = (setup) => {
    const unitNumberText = setup.apartmentUnit ? `, Unit ${setup.apartmentUnit}` : '';
    const address = `${setup.addressStreetDisplay}${unitNumberText}`;
    return (
      <SrBox width="300px" overflow="hidden" textOverflow="ellipsis">
        <SrText font="greycliff" fontSize="16px" weight="800" whiteSpace="nowrap">
          {address}
        </SrText>
        <SrText
          font="greycliff"
          fontSize="14px"
          paddingLeft="7px"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
        >
          {`${setup.addressCity}, ${setup.addressStateCode}`}
        </SrText>
      </SrBox>
    );
  };

  const _getIsLiveTagComponent = (setup) => {
    if (setup.status === LISTING_STATUS_ACTIVE) {
      return (
        <SrTag theme={SR_TAG_THEME_LIGHTGREEN_GREEN} variation="completedSetups">
          Live
        </SrTag>
      );
    }
    return (
      <SrTag theme={SR_TAG_THEME_RED_WHITE} variation="completedSetups">
        Not Live
      </SrTag>
    );
  };

  const _getIsTourableTagComponent = (setup) => {
    if (setup.cantTourFlag) {
      return (
        <SrTag theme={SR_TAG_THEME_RED_WHITE} variation="completedSetups">
          Not Tourable
        </SrTag>
      );
    }
    return (
      <SrTag theme={SR_TAG_THEME_LIGHTGREEN_GREEN} variation="completedSetups">
        Tourable
      </SrTag>
    );
  };

  const _getIsVacantTagComponent = (setup) => {
    if (setup.occupantType === LISTING_OCCUPANT_TYPE_VACANT) {
      return (
        <SrTag theme={SR_TAG_THEME_WHITE_BLACK} variation="completedSetups">
          Vacant
        </SrTag>
      );
    }
    return (
      <SrTag theme={SR_TAG_THEME_ORANGE_WHITE} variation="completedSetups">
        Occupied
      </SrTag>
    );
  };

  const onNoteClick = (event, setup) => {
    event.preventDefault();

    setListingNotesModal(setup);
  };

  const _getNotesTagComponent = (setup) => {
    const hasNotes = setup.notesCount > 0;
    const blueTheme = isAdmin ? SR_TAG_THEME_LIGHTBLUE_BLACK_HOVER : SR_TAG_THEME_LIGHTBLUE_BLACK;
    const theme = hasNotes ? blueTheme : SR_TAG_THEME_WHITE_BLACK;
    const onTagClick = isAdmin ? (e) => onNoteClick(e, setup) : undefined;

    return (
      <SrTag theme={theme} variation="completedSetups" onClick={onTagClick}>
        {`${pluralize('Note', setup.notesCount, true)}`}
      </SrTag>
    );
  };

  const _getDaysOnMarketTagComponent = (setup) => {
    let theme = SR_TAG_THEME_WHITE_BLACK;

    if (setup.daysOnMarket > 30 && setup.daysOnMarket <= 60) {
      theme = SR_TAG_THEME_ORANGE_WHITE;
    }
    if (setup.daysOnMarket > 60) {
      theme = SR_TAG_THEME_RED_WHITE;
    }

    return (
      <SrTag theme={theme} variation="completedSetups">
        {setup.daysOnMarket} DOM
      </SrTag>
    );
  };

  const _getSetupTagsComponent = (setup) => {
    const tags = [
      _getIsLiveTagComponent(setup),
      _getIsTourableTagComponent(setup),
      _getIsVacantTagComponent(setup),
      _getDaysOnMarketTagComponent(setup),
      _getNotesTagComponent(setup),
    ];

    return (
      <SrFlex width="auto" minWidth="270px" maxWidth="345px">
        {tags}
      </SrFlex>
    );
  };

  const _getOwnersText = (setup) => {
    const owners = lodashCompact([setup.propertyOwnerName, setup.propertySecondOwnerName]);

    return owners.join(', ');
  };

  const _getOwnersComponent = (setup) => {
    const iconName =
      setup.propertyOwnerType === LISTING_PROPERTY_OWNER_INSTITUTION
        ? 'property-owner-individual'
        : 'property-owner-pm';

    const ownersText = _getOwnersText(setup);

    return (
      <SrFlex align="baseline" minWidth="250px" marginLeft="20px">
        <SrIcon className={iconName} marginRight="5px" />
        <SrBox>
          <SrText
            font="greycliff"
            fontSize="14px"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            overflow="hidden"
          >
            {ownersText}
          </SrText>
        </SrBox>
      </SrFlex>
    );
  };

  const _getCompletedSetupRows = () => {
    if (completedSetups.length === 0) {
      return null;
    }

    return completedSetups.map((setup) => {
      const setupAddressComponent = _getSetupAddressComponent(setup);
      const setupTagsComponent = _getSetupTagsComponent(setup);
      const ownersComponent = _getOwnersComponent(setup);
      const isLive = setup.status === LISTING_STATUS_ACTIVE;
      const isTourable = !setup.cantTourFlag;
      return (
        <SrFlexRow key={setup.listingId} marginTop="2px" wrap="nowrap">
          <SrFlex styleName="setupRow">
            <SrLink
              to={`/partner/${partnerSlug}/listing/${setup.listingId}`}
              styleName="setupContent"
            >
              <SrFlex width="calc(100% - 80px)" align="center" paddingLeft="20px">
                <SrBox>{setupAddressComponent}</SrBox>
                <SrBox>{setupTagsComponent}</SrBox>
                <SrBox>{ownersComponent}</SrBox>
              </SrFlex>
            </SrLink>
            <SrFlex styleName="buttonEllipsis" align="center" justify="center">
              <SrText font="greycliff" fontSize="12px" weight="800" paddingBottom="8px">
                ...
              </SrText>
              <SrBox bgColor="white" styleName="actionsContainer">
                <div
                  styleName="action"
                  onClick={() =>
                    handleIsLiveUpdate({
                      listingId: setup.listingId,
                    })
                  }
                >
                  <SrText font="greycliff" fontSize="13px" weight="700">
                    {isLive ? "Mark 'Not Live'" : "Mark 'Live'"}
                  </SrText>
                </div>
                <div
                  styleName="action"
                  onClick={() =>
                    handleIsTourableUpdate({
                      listingId: setup.listingId,
                    })
                  }
                >
                  <SrText font="greycliff" fontSize="13px" weight="700">
                    {isTourable ? "Mark 'Can't Tour'" : "Mark 'Can Tour'"}
                  </SrText>
                </div>
              </SrBox>
            </SrFlex>
          </SrFlex>
        </SrFlexRow>
      );
    });
  };

  const onCloseNotesModal = () => {
    reloadItems({
      count: 1,
      filter: {
        'listings.id': listingNotesModal.id,
      },
    });
    setListingNotesModal(null);
  };

  const completedSetupRows = _getCompletedSetupRows();
  return (
    <>
      <SrFlex direction="col" styleName="SrCompletedSetupsTable">
        {completedSetupRows}
      </SrFlex>
      {listingNotesModal && (
        <SrModalV2
          closeOnBackgroundClick
          showCloseButton={false}
          contentMaxWidth="546px"
          contentPadding="0px"
          theme={MODAL_THEME_STACK}
          onClose={onCloseNotesModal}
        >
          <SrCard header="Notes (Admin-Only)" color={SR_CARD_BULLET_COLOR_BLUE} maxWidth="none">
            <SrAdminListingNotes
              listingId={listingNotesModal.id}
              dispatch={dispatch}
              currentUser={currentUser}
            />
          </SrCard>
        </SrModalV2>
      )}
    </>
  );
};

SrCompletedSetupsTable.propTypes = {
  completedSetups: PropTypes.array.isRequired,
  handleIsTourableUpdate: PropTypes.func.isRequired,
  handleIsLiveUpdate: PropTypes.func.isRequired,
  partnerSlug: PropTypes.string.isRequired,
  reloadItems: PropTypes.func,
};

export default SrCompletedSetupsTable;
